import { graphql, navigate, useStaticQuery } from "gatsby";
import React, { useEffect, useState } from "react";

import Image from "../../components/image";
import { useTheme } from "../../context/ThemeContext";
import { useBreakpoint } from "../../hooks";
import Logo from "../../images/gvi-logo2.inline.svg";
import ArrowRight from "../../images/icons/arrow-right.inline.svg";
import { Link } from "../../utils/customLink";
import { siteSwitcherEnabledPageList } from "../../utils/sharedDatasets";
import ApplyButton from "../shared/applyButton";

const SiteSwitcherPopup = () => {
  const { peopleDomain } = useTheme();

  const { communityImage, conservationImage } = useStaticQuery(graphql`
    {
      conservationImage: file(name: { eq: "conservation" }) {
        ...SiteSwitcherImageFragment
        # childImageSharp {
        #   gatsbyImageData(formats: WEBP, quality: 90, layout: FULL_WIDTH)
        # }
      }
      communityImage: file(name: { eq: "community" }) {
        ...SiteSwitcherImageFragment
        # childImageSharp {
        #   gatsbyImageData(formats: WEBP, quality: 90, layout: FULL_WIDTH)
        # }
      }
    }
  `);

  const [showSiteSwitcherPopup, setShowSiteSwitcherPopup] = useState(
    (typeof window !== "undefined" &&
      window.localStorage.getItem("showSiteSwitcherPopup")) ??
      true
  );

  const [showPopupOnPage, setShowPopupOnPage] = useState(false);

  const [currentLocation, setCurrentLocation] = useState("/");
  const handleSiteSwitcher = () => {
    setShowSiteSwitcherPopup(false);
    if (typeof window !== "undefined") {
      window.localStorage.setItem("showSiteSwitcherPopup", false);
    }
  };

  useEffect(() => {
    const url = new URL(window.location);

    const listPages = siteSwitcherEnabledPageList;

    // console.debug("url.pathname", url.pathname, url.search);
    setCurrentLocation(url.pathname);

    //if its a manual switch from people, then don't show the site switcher
    if (url.search.includes("nav_switcher")) {
      handleSiteSwitcher();
    }

    //are we showing the blocker here?
    setShowPopupOnPage(
      !url.search.includes("nav_switcher") && listPages.includes(url.pathname)
    );
  }, []);

  const breakpoint = useBreakpoint();

  const communityTitle = "Community";
  const communityDesc = {
    desktop:
      "Form bonds beyond borders. Travel to communities around the world with GVI People to immerse yourself in different cultures, and contribute to impact alongside local partners.",
    mobile:
      "Immerse yourself in different cultures and contribute to sustainable impact."
  };
  const communityCTAText = {
    desktop: "Community programs",
    mobile: "Community programs"
  };

  const conservationPopupTitle = "Conservation";
  const conservationPopupDesc = {
    desktop:
      "Live your wildest dreams. Go on an adventure with GVI Planet and contribute to nature conservation efforts in vulnerable habitats all over the world.",
    mobile:
      "Contribute to nature conservation efforts in vulnerable habitats all over the world."
  };
  const conservationPopupCTAText = {
    desktop: "Conservation programs",
    mobile: "Conservation programs"
  };

  const experienceCTAText = {
    title: "Tailor your experience",
    subtitle: "Switching between the two options is easy!"
  };

  // console.debug("breakpoint", breakpoint);
  // console.debug("showPopupOnPage", { communityImage, conservationImage });

  if (
    process.env.GATSBY_PROJECT_NAME.toLowerCase().includes("planet") &&
    showSiteSwitcherPopup === true &&
    showPopupOnPage === true
  ) {
    return (
      <div className="wrapper h-svh fixed top-0 left-0 right-0 bottom-0 flex flex-col z-50 gvi-modal-bg justify-center items-center sm:h-full sm:flex-row">
        <div className="h-full my-5 w-11/12 sm:h-auto sm:w-3/4 2xl:container bg-primary-bg grid grid-cols-none grid-rows-8 sm:grid-rows-2 sm:grid-cols-2 gap-0 rounded-lg overflow-hidden relative">
          <div className="bg-white relative z-[2] flex justify-center py-5 left-0 right-0 row-span-1 sm:absolute sm:row-span-3 sm:col-span-2 sm:pt-20  sm:bg-transparent">
            <div className="sm:text-white text-center justify-center items-center flex flex-col">
              <div
                className="text-base xxxs:text-2xl xs:text-3xl sm:text-4xl font-bold "
                dangerouslySetInnerHTML={{ __html: experienceCTAText.title }}
              />
              <div
                className="font-light xxxs:text-xs xxs:text-base sm:py-2"
                dangerouslySetInnerHTML={{ __html: experienceCTAText.subtitle }}
              />
              <div className="divider border-t-2 border-white w-1/4 mx-auto my-2 hidden sm:block" />
            </div>
          </div>
          <div className="h-auto sm:h-100 md:h-105 bg-primary-bg relative row-span-3">
            <Image
              imageAlt={""}
              image={conservationImage}
              imageClass="object-fill object-center absolute left-0 top-0 bottom-0 right-0 z-0 w-full"
              sourceUrl={conservationImage?.publicURL}
            />
            <div className="flex h-full flex-col z-10 top-0 right-0 left-0 bottom-0 absolute">
              <div className="flex items-center justify-center m-auto py-3 xs:pt-44 xs:pb-5 xxxs:py-1">
                <Link to={currentLocation} onClick={() => handleSiteSwitcher()}>
                  <Logo className="w-[39px] sm:w-[57px] fill-black-lighter planet:fill-white" />
                </Link>
                <Link to={currentLocation} onClick={() => handleSiteSwitcher()}>
                  <p className="h7 menu-link pl-7 text-white !font-semibold uppercase">
                    Planet
                  </p>
                </Link>
                <ArrowRight className="w-3 h-3 ml-1 stroke-green-standard gvi-text-green group-hover:rotate-90" />
              </div>
              <div className="center flex h-full flex-col items-center justify-evenly gap-1 sm:gap-5">
                <div className="content hidden xxs:block">
                  <div
                    className="font-highlight text-white text-center subtle-shadow tracking-normal text-4xl sm:text-6xl lg:text-8xl"
                    dangerouslySetInnerHTML={{ __html: conservationPopupTitle }}
                  />
                </div>
                <div className="bottom text-center w-4/5 sm:w-1/2 block">
                  <ApplyButton
                    to={currentLocation}
                    buttonStyle="group relative z-10 flex flex-row items-center justify-center px-2 py-2 rounded-full planet:rounded-sm text-white planet:text-gray-darker bg-red-light planet:bg-green-standard hover:bg-black-lighter planet:hover:bg-white"
                    ctaLabel={
                      breakpoint === "xs"
                        ? conservationPopupCTAText.mobile
                        : conservationPopupCTAText.desktop
                    }
                    labelClass="text-base leading-genericCta font-bold pr-2"
                    arrowClass="hover:stroke-black-lighter stroke-black-lighter"
                    onClick={() => handleSiteSwitcher()}
                  />
                </div>
              </div>
            </div>
            <div
              onClick={() => handleSiteSwitcher()}
              className="absolute z-10 top-0 left-0 right-0 bottom-0 cursor-pointer"
            />
          </div>
          <div className="h-auto sm:h-100 md:h-105 bg-primary-bg relative row-span-3">
            <Image
              imageAlt={""}
              image={communityImage}
              imageClass="object-fill absolute left-0 top-0 bottom-0 right-0 z-0 w-full"
              sourceUrl={communityImage?.publicURL}
            />
            <div className="flex h-full flex-col  z-10 top-0 right-0 left-0 bottom-0 absolute">
              <div className="flex items-center justify-center m-auto py-3 xs:pt-44 xs:pb-5 xxxs:py-1">
                <Link to={[peopleDomain, currentLocation].join("")}>
                  <Logo className="w-[39px] sm:w-[57px] fill-black-lighter planet:fill-white" />
                </Link>
                <Link to={[peopleDomain, currentLocation].join("")}>
                  <p className="h7 menu-link pl-7 text-white !font-semibold uppercase">
                    People
                  </p>
                </Link>
                <ArrowRight className="w-3 h-3 ml-1 stroke-red-light gvi-text-green group-hover:rotate-90" />
              </div>
              <div className="center flex h-full flex-col items-center justify-evenly gap-1 sm:gap-5">
                <div className="content hidden xxs:block">
                  <div
                    className="font-highlight text-white text-center subtle-shadow tracking-normal text-4xl sm:text-6xl lg:text-8xl"
                    dangerouslySetInnerHTML={{ __html: communityTitle }}
                  />
                </div>
                <div className="bottom text-center w-4/5 sm:w-1/2 block">
                  <ApplyButton
                    to={[peopleDomain, currentLocation].join("")}
                    buttonStyle="group relative z-10 flex flex-row items-center justify-center px-2 py-2 rounded-full text-white bg-red-light hover:bg-black-lighter"
                    ctaLabel={
                      breakpoint === "xs"
                        ? communityCTAText.mobile
                        : communityCTAText.desktop
                    }
                    labelClass="text-base leading-genericCta font-bold pr-2"
                    arrowClass="stroke-white hover:stroke-black-lighter"
                    onClick={() => {}}
                  />
                </div>
              </div>
            </div>
            <Link
              to={[peopleDomain, currentLocation].join("")}
              className="absolute z-10 top-0 left-0 right-0 bottom-0"
            />
          </div>
          <div className="sm:col-span-2 bg-white sm:h-11 leading-tight p-2 flex flex-wrap flex-row items-center justify-center relative row-span-1 sm:flex-col">
            <p className="text-center xxxs:text-xs xxs:text-base">
              Not 100% sure yet? &nbsp; <br className="sm:hidden" />
              <span
                className="font-bold underline cursor-pointer"
                onClick={() => {
                  handleSiteSwitcher();
                  navigate("/volunteer-abroad/");
                }}
              >
                Explore more
              </span>
              &nbsp; or arrange a &nbsp;
              <span
                className="font-bold underline cursor-pointer"
                onClick={() => {
                  handleSiteSwitcher();
                  navigate("/enquiry-form/?consultation");
                }}
              >
                free consultation
              </span>
              &nbsp; with one of our program experts.
            </p>
          </div>
        </div>
      </div>
    );
  } else {
    return <></>;
  }
};

export default SiteSwitcherPopup;
